import { Dataset } from '../types';

// Generate semi-random data with correlations between some dimensions
const generateDataset = (): Dataset => {
  // Define dimensions
  const dimensions = [
    { id: 'dim1', name: 'Temperature', type: 'numeric' as const, domain: [0, 100] as [number, number] },
    { id: 'dim2', name: 'Pressure', type: 'numeric' as const, domain: [0, 1000] as [number, number] },
    { id: 'dim3', name: 'Humidity', type: 'numeric' as const, domain: [0, 100] as [number, number] },
    { id: 'dim4', name: 'Wind Speed', type: 'numeric' as const, domain: [0, 50] as [number, number] },
    { id: 'dim5', name: 'Precipitation', type: 'numeric' as const, domain: [0, 500] as [number, number] },
    { id: 'dim6', name: 'Visibility', type: 'numeric' as const, domain: [0, 10] as [number, number] },
    { id: 'dim7', name: 'Air Quality', type: 'numeric' as const, domain: [0, 500] as [number, number] },
    { id: 'dim8', name: 'UV Index', type: 'numeric' as const, domain: [0, 12] as [number, number] },
    { id: 'dim9', name: 'Cloud Cover', type: 'numeric' as const, domain: [0, 100] as [number, number] },
    { id: 'dim10', name: 'Dew Point', type: 'numeric' as const, domain: [-20, 30] as [number, number] },
    { id: 'dim11', name: 'Season', type: 'categorical' as const, categories: ['Spring', 'Summer', 'Fall', 'Winter'] },
    {
      id: 'dim12',
      name: 'Time of Day',
      type: 'categorical' as const,
      categories: ['Morning', 'Afternoon', 'Evening', 'Night'],
    },
  ];

  // Generate items with correlations
  const items = Array.from({ length: 100 }, (_, i) => {
    // Base values
    const temp = Math.random() * 100; // Temperature
    const season = ['Spring', 'Summer', 'Fall', 'Winter'][Math.floor(Math.random() * 4)];
    const timeOfDay = ['Morning', 'Afternoon', 'Evening', 'Night'][Math.floor(Math.random() * 4)];

    // Create correlations between dimensions
    const pressure = 500 + temp * 3 + Math.random() * 200; // Correlation with temperature
    const humidity = season === 'Summer' ? 50 + Math.random() * 50 : Math.max(0, 100 - temp + Math.random() * 30); // Inverse correlation with temperature in non-summer

    const windSpeed = 5 + Math.random() * 45;

    const precipitation = humidity > 70 ? 200 + Math.random() * 300 : Math.random() * 100; // Correlation with humidity

    const visibility = 10 - precipitation / 100 + Math.random() * 3; // Inverse correlation with precipitation

    const airQuality = 100 + windSpeed * 2 + Math.random() * 300; // Some correlation with wind speed

    const uvIndex = season === 'Summer' ? 8 + Math.random() * 4 : Math.random() * 8; // Higher in summer

    const cloudCover = Math.min(100, humidity + Math.random() * 30); // Correlation with humidity

    const dewPoint = humidity / 5 - 10 + Math.random() * 10; // Correlation with humidity

    return {
      id: `item-${i + 1}`,
      dim1: temp,
      dim2: pressure,
      dim3: humidity,
      dim4: windSpeed,
      dim5: precipitation,
      dim6: visibility,
      dim7: airQuality,
      dim8: uvIndex,
      dim9: cloudCover,
      dim10: dewPoint,
      dim11: season,
      dim12: timeOfDay,
      label: `Data point ${i + 1}`,
    };
  });

  return {
    id: 'weather-data',
    name: 'Weather Measurements',
    description: 'Sample weather dataset with multiple correlated dimensions',
    dimensions,
    items,
  };
};

// Car dataset with multiple dimensions
const generateCarDataset = (): Dataset => {
  // Define dimensions
  const dimensions = [
    { id: 'price', name: 'Price ($)', type: 'numeric' as const, domain: [5000, 100000] as [number, number] },
    { id: 'mileage', name: 'Mileage', type: 'numeric' as const, domain: [0, 200000] as [number, number] },
    { id: 'mpg', name: 'MPG', type: 'numeric' as const, domain: [10, 50] as [number, number] },
    { id: 'horsepower', name: 'Horsepower', type: 'numeric' as const, domain: [70, 400] as [number, number] },
    { id: 'weight', name: 'Weight (lbs)', type: 'numeric' as const, domain: [2000, 5000] as [number, number] },
    { id: 'acceleration', name: '0-60 (sec)', type: 'numeric' as const, domain: [3, 15] as [number, number] },
    { id: 'year', name: 'Year', type: 'numeric' as const, domain: [1990, 2023] as [number, number] },
    { id: 'reliability', name: 'Reliability', type: 'numeric' as const, domain: [1, 10] as [number, number] },
    { id: 'comfort', name: 'Comfort', type: 'numeric' as const, domain: [1, 10] as [number, number] },
    {
      id: 'make',
      name: 'Make',
      type: 'categorical' as const,
      categories: ['Toyota', 'Honda', 'Ford', 'BMW', 'Audi', 'Mercedes', 'Chevrolet', 'Nissan'],
    },
    {
      id: 'type',
      name: 'Type',
      type: 'categorical' as const,
      categories: ['Sedan', 'SUV', 'Truck', 'Sports', 'Hybrid', 'Electric'],
    },
  ];

  // Generate items with correlations
  const items = Array.from({ length: 100 }, (_, i) => {
    // Generate make and type
    const makes = ['Toyota', 'Honda', 'Ford', 'BMW', 'Audi', 'Mercedes', 'Chevrolet', 'Nissan'];
    const types = ['Sedan', 'SUV', 'Truck', 'Sports', 'Hybrid', 'Electric'];

    const make = makes[Math.floor(Math.random() * makes.length)];
    const type = types[Math.floor(Math.random() * types.length)];

    // Base year affects many other attributes
    const year = Math.floor(1990 + Math.random() * 33);

    // Luxury brands have higher prices
    const luxuryBrands = ['BMW', 'Audi', 'Mercedes'];
    const luxuryMultiplier = luxuryBrands.includes(make) ? 1.5 : 1;

    // Type affects various features
    let typeMultiplier = 1;
    switch (type) {
      case 'SUV':
        typeMultiplier = 1.2;
        break;
      case 'Truck':
        typeMultiplier = 1.3;
        break;
      case 'Sports':
        typeMultiplier = 1.5;
        break;
      case 'Hybrid':
        typeMultiplier = 1.1;
        break;
      case 'Electric':
        typeMultiplier = 1.4;
        break;
    }

    // Calculate attributes with correlations
    const basePrice = 15000 + (year - 1990) * 500;
    const price = basePrice * luxuryMultiplier * typeMultiplier + Math.random() * 10000;

    const mileage = Math.max(0, 100000 - (year - 1990) * 5000 + Math.random() * 50000);

    // MPG is better for newer cars and certain types
    let mpgBase = 15 + (year - 1990) / 5;
    if (type === 'Hybrid') mpgBase += 15;
    if (type === 'Electric') mpgBase = 40;
    const mpg = mpgBase + Math.random() * 5;

    // Horsepower varies by type and make
    let hpBase = 100;
    if (type === 'Sports') hpBase = 250;
    if (type === 'Truck') hpBase = 200;
    if (luxuryBrands.includes(make)) hpBase += 50;
    const horsepower = hpBase + Math.random() * 100;

    // Weight varies by type
    let weightBase = 3000;
    if (type === 'SUV') weightBase = 3500;
    if (type === 'Truck') weightBase = 4000;
    if (type === 'Sports') weightBase = 2800;
    const weight = weightBase + Math.random() * 500;

    // Acceleration is correlated with horsepower and weight
    const acceleration = 15 - horsepower / 100 + weight / 2000 + Math.random() * 2;

    // Reliability and comfort vary by make and year
    let reliabilityBase = 5;
    if (['Toyota', 'Honda'].includes(make)) reliabilityBase += 2;
    reliabilityBase += (year - 1990) / 20;
    const reliability = Math.min(10, reliabilityBase + Math.random() * 2);

    let comfortBase = 5;
    if (luxuryBrands.includes(make)) comfortBase += 2;
    if (type === 'SUV') comfortBase += 1;
    comfortBase += (year - 1990) / 20;
    const comfort = Math.min(10, comfortBase + Math.random() * 2);

    return {
      id: `car-${i + 1}`,
      price,
      mileage,
      mpg,
      horsepower,
      weight,
      acceleration,
      year,
      reliability,
      comfort,
      make,
      type,
      label: `${make} ${type} (${year})`,
    };
  });

  return {
    id: 'car-data',
    name: 'Car Specifications',
    description: 'Dataset of car specifications with various attributes',
    dimensions,
    items,
  };
};

export const datasets: Record<string, Dataset> = {
  weatherData: generateDataset(),
  carData: generateCarDataset(),
};
